import React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  TextField as MuiTextField,
  Box,
  Stack,
  Tooltip,
  Popover,
  IconButton,
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  FaStar,
  FaUserGraduate,
  FaUsers,
  FaLightbulb,
  FaHandshake,
  FaComments,
  FaInfoCircle,
  FaQuestionCircle,
} from "react-icons/fa";
import { MdSchool, MdAssignment } from "react-icons/md";
import api from "../../../api/axiosInstance";
import { Button as Btn } from "../../../components/atoms/Button";
import { useSnackbar } from "../../../context/SnackbarContext";
import theme from "../../../theme";
import type { ColorsCodeType } from "../../../typings";
import { InterviewGuidelinesModal } from "./interview-guidelines-modal";

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  applicationId: number;
  onRefresh: () => void;
}

interface InterviewFeedback {
  scholarship_application_id: number;
  commitment_to_studies_rating: number;
  leadership_potential_rating: number;
  community_involvement_rating: number;
  initiative_and_creativity_rating: number;
  alignment_with_scholarship_objectives_rating: number;
  vision_for_future_contribution_to_society_rating: number;
  comments_related_to_academic_performance: string;
  comments_related_to_leadership: string;
  comments_related_to_values: string;
  final_remarks: string;
}

const validationSchema = Yup.object({
  commitment_to_studies_rating: Yup.number()
    .required("Required")
    .min(1, "Please select a rating"),
  leadership_potential_rating: Yup.number()
    .required("Required")
    .min(1, "Please select a rating"),
  community_involvement_rating: Yup.number()
    .required("Required")
    .min(1, "Please select a rating"),
  initiative_and_creativity_rating: Yup.number()
    .required("Required")
    .min(1, "Please select a rating"),
  alignment_with_scholarship_objectives_rating: Yup.number()
    .required("Required")
    .min(1, "Please select a rating"),
  vision_for_future_contribution_to_society_rating: Yup.number()
    .required("Required")
    .min(1, "Please select a rating"),
  comments_related_to_academic_performance: Yup.string().required(
    "Please provide comments on academic performance"
  ),
  comments_related_to_leadership: Yup.string().required(
    "Please provide comments on leadership"
  ),
  comments_related_to_values: Yup.string().required(
    "Please provide comments on values"
  ),
  final_remarks: Yup.string().required("Please provide final remarks"),
});

// Rating scale definitions
const ratingScaleDefinitions = {
  commitment_to_studies_rating: [
    {
      value: 1,
      label: "Low Commitment",
      description:
        "Rarely studies, inconsistent, lacks focus, and prioritizes other activities over academics.",
    },
    {
      value: 2,
      label: "Below Average Commitment",
      description:
        "Studies occasionally, but lacks a structured approach or clear goals. Motivation is inconsistent.",
    },
    {
      value: 3,
      label: "Moderate Commitment",
      description:
        "Studies regularly but may not be fully consistent or dedicated. Shows some effort but could improve focus.",
    },
    {
      value: 4,
      label: "High Commitment",
      description:
        "Consistently studies with a structured approach, prioritizes academics, and stays motivated most of the time.",
    },
    {
      value: 5,
      label: "Exceptional Commitment",
      description:
        "Fully dedicated to studies, highly disciplined, and demonstrates unwavering focus and effort.",
    },
  ],
  leadership_potential_rating: [
    {
      value: 1,
      label: "Low Potential",
      description:
        "Rarely takes initiative, avoids responsibility, and struggles to influence or inspire others.",
    },
    {
      value: 2,
      label: "Below Average Potential",
      description:
        "Occasionally takes initiative but lacks consistency, confidence, or the ability to guide a team effectively.",
    },
    {
      value: 3,
      label: "Moderate Potential",
      description:
        "Sometimes shows initiative, can lead in familiar situations, but needs development in inspiring or managing diverse teams.",
    },
    {
      value: 4,
      label: "High Potential",
      description:
        "Frequently takes initiative, demonstrates strong decision-making, and effectively inspires and guides others.",
    },
    {
      value: 5,
      label: "Exceptional Potential",
      description:
        "Consistently displays natural leadership skills, inspires trust, motivates teams, and excels in decision-making and problem-solving.",
    },
  ],
  community_involvement_rating: [
    {
      value: 1,
      label: "Low Involvement",
      description:
        "Rarely participates in community activities or contributes to initiatives.",
    },
    {
      value: 2,
      label: "Below Average Involvement",
      description:
        "Occasionally engages in community activities but with minimal effort or irregular participation.",
    },
    {
      value: 3,
      label: "Moderate Involvement",
      description:
        "Participates in community activities regularly but primarily in a supportive or passive role.",
    },
    {
      value: 4,
      label: "High Involvement",
      description:
        "Actively engages in community initiatives, takes responsibility, and contributes meaningfully.",
    },
    {
      value: 5,
      label: "Exceptional Involvement",
      description:
        "Consistently leads or organizes impactful community activities, inspires others to participate, and demonstrates a strong commitment to social betterment.",
    },
  ],
  initiative_and_creativity_rating: [
    {
      value: 1,
      label: "Low Initiative/Creativity",
      description:
        "Rarely takes initiative or contributes new ideas. Prefers to follow established methods without innovation.",
    },
    {
      value: 2,
      label: "Below Average",
      description:
        "Occasionally suggests ideas or takes initiative but lacks consistency or originality in approach.",
    },
    {
      value: 3,
      label: "Moderate",
      description:
        "Sometimes takes initiative and contributes creative ideas, but mainly in familiar or guided contexts.",
    },
    {
      value: 4,
      label: "High",
      description:
        "Regularly takes initiative and proposes innovative solutions, showing originality and proactive thinking.",
    },
    {
      value: 5,
      label: "Exceptional",
      description:
        "Consistently takes bold initiative, demonstrates exceptional creativity, and inspires others with groundbreaking ideas.",
    },
  ],
  alignment_with_scholarship_objectives_rating: [
    {
      value: 1,
      label: "Low Alignment",
      description:
        "Demonstrates minimal understanding or connection to the scholarship's goals and values.",
    },
    {
      value: 2,
      label: "Below Average Alignment",
      description:
        "Shows some awareness of the scholarship's objectives but lacks clear alignment with its purpose.",
    },
    {
      value: 3,
      label: "Moderate Alignment",
      description:
        "Understands the scholarship's objectives and shows partial alignment, but could articulate goals more clearly.",
    },
    {
      value: 4,
      label: "High Alignment",
      description:
        "Strongly aligns with the scholarship's goals, clearly articulates how it supports personal and career objectives.",
    },
    {
      value: 5,
      label: "Exceptional Alignment",
      description:
        "Perfectly aligns with the scholarship's purpose, demonstrating a deep understanding and clear contribution to its objectives.",
    },
  ],
  vision_for_future_contribution_to_society_rating: [
    {
      value: 1,
      label: "Low Contribution Potential",
      description:
        "Minimal interest or plans to contribute to society in a meaningful way in the future.",
    },
    {
      value: 2,
      label: "Below Average Contribution Potential",
      description:
        "Some awareness of societal issues, but lacks clear or actionable plans for future contributions.",
    },
    {
      value: 3,
      label: "Moderate Contribution Potential",
      description:
        "Shows a willingness to contribute to society, with some ideas or general plans for making an impact.",
    },
    {
      value: 4,
      label: "High Contribution Potential",
      description:
        "Demonstrates strong commitment and well-thought-out plans to address societal challenges or make a positive impact.",
    },
    {
      value: 5,
      label: "Exceptional Contribution Potential",
      description:
        "Deeply committed to societal betterment with concrete, actionable plans and a clear vision for long-term impact.",
    },
  ],
};

export default function FeedbackModal({
  open,
  onClose,
  applicationId,
  onRefresh,
}: FeedbackModalProps) {
  const { showSnackbar } = useSnackbar();
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [guidelinesOpen, setGuidelinesOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeRatingField, setActiveRatingField] = useState<string | null>(
    null
  );

  const initialValues: InterviewFeedback = {
    scholarship_application_id: applicationId,
    commitment_to_studies_rating: 0,
    leadership_potential_rating: 0,
    community_involvement_rating: 0,
    initiative_and_creativity_rating: 0,
    alignment_with_scholarship_objectives_rating: 0,
    vision_for_future_contribution_to_society_rating: 0,
    comments_related_to_academic_performance: "",
    comments_related_to_leadership: "",
    comments_related_to_values: "",
    final_remarks: "",
  };

  const handleSubmit = async (
    values: InterviewFeedback,
    { resetForm }: { resetForm: () => void }
  ) => {
    try {
      setSubmitStatus("loading");
      await api.post(
        "/scholarship-applications/interview-feedback/",
        values
      );

      showSnackbar("Feedback submitted successfully!", "success");
      resetForm();
      
      // Refresh the applications list
      onRefresh();

      // Close the modal after successful submission
      setTimeout(() => {
        onClose();
        setSubmitStatus("idle");
      }, 1500);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "An unknown error occurred";
      showSnackbar(errorMessage, "error");
      setSubmitStatus("error");

      // Reset error state after some time
      setTimeout(() => {
        setSubmitStatus("idle");
        setErrorMessage("");
      }, 3000);
    }
  };



  const handleOpenRatingInfo = (
    event: React.MouseEvent<HTMLElement>,
    fieldName: string
  ) => {
    setAnchorEl(event.currentTarget);
    setActiveRatingField(fieldName);
  };

  const handleCloseRatingInfo = () => {
    setAnchorEl(null);
    setActiveRatingField(null);
  };

  const openRatingInfo = Boolean(anchorEl);

  const RatingField = ({
    name,
    label,
    icon,
  }: {
    name: string;
    label: string;
    icon: React.ReactNode;
  }) => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography
          component="label"
          sx={{
            display: "flex",
            alignItems: "center",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          <Box component="span" sx={{ mr: 1 }}>
            {icon}
          </Box>
          {label}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => handleOpenRatingInfo(e, name)}
          sx={{
            ml: 1,
            color: "#1976d2",
            "&:hover": {
              backgroundColor: "rgba(99, 102, 241, 0.1)",
            },
          }}
        >
          <FaQuestionCircle size={16} />
        </IconButton>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Field name={name}>
          {({ field, form }: any) => (
            <Box sx={{ display: "flex" }}>
              {[1, 2, 3, 4, 5].map((star) => {
                const definition =
                  ratingScaleDefinitions[
                    name as keyof typeof ratingScaleDefinitions
                  ]?.[star - 1];
                return (
                  <Tooltip
                    key={star}
                    title={
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
                          {definition?.label || `Rating ${star}`}
                        </Typography>
                        <Typography sx={{ fontSize: "12px" }}>
                          {definition?.description}
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="top"
                  >
                    <Box
                      onClick={() => form.setFieldValue(field.name, star)}
                      sx={{
                        cursor: "pointer",
                        padding: "2px",
                        transition: "transform 0.2s",
                        "&:hover": {
                          transform: "scale(1.2)",
                        },
                      }}
                    >
                      <FaStar
                        size={24}
                        style={{
                          color: field.value >= star ? "#1976d2" : "#d1d5db",
                        }}
                      />
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          )}
        </Field>
      </Box>
      <ErrorMessage name={name}>
        {(msg) => (
          <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
            {msg}
          </Typography>
        )}
      </ErrorMessage>
    </Box>
  );

  const TextFieldComponent = ({
    name,
    label,
    placeholder,
    icon,
    multiline = true,
    rows = 3,
  }: {
    name: string;
    label: string;
    placeholder: string;
    icon?: React.ReactNode;
    multiline?: boolean;
    rows?: number;
  }) => (
    <Box sx={{ mb: 2 }}>
      <Typography
        component="label"
        htmlFor={name}
        sx={{
          display: "flex",
          alignItems: "center",
          fontSize: "14px",
          fontWeight: 500,
          mb: 1,
        }}
      >
        {icon && (
          <Box component="span" sx={{ mr: 1 }}>
            {icon}
          </Box>
        )}
        {label}
      </Typography>
      <Field name={name}>
        {({ field, meta }: any) => (
          <>
            <MuiTextField
              id={name}
              {...field}
              placeholder={placeholder}
              multiline={multiline}
              rows={rows}
              fullWidth
              variant="outlined"
              error={meta.touched && meta.error ? true : false}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  fontSize: "14px",
                  "&.Mui-focused fieldset": {
                    borderColor: "#1976d2",
                  },
                },
              }}
            />
            {meta.touched && meta.error && (
              <Typography
                color="error"
                variant="caption"
                sx={{ mt: 0.5, display: "block" }}
              >
                {meta.error}
              </Typography>
            )}
          </>
        )}
      </Field>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            maxWidth: "650px",
            width: "100%",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
            backgroundColor: "white",
          },
        }}
      >
        <DialogTitle
          sx={{
            padding: "24px 24px 0",
            fontSize: "24px",
            fontWeight: 600,
            color: "#1e293b",
          }}
        >
          Interview Feedback
        </DialogTitle>
        <Button
          startIcon={<FaInfoCircle />}
          onClick={() => setGuidelinesOpen(true)}
          sx={{
            textTransform: "none",
            margin: "0 24px",
            backgroundColor: "#f0f7ff",
            color: "#1976d2",
            borderRadius: "20px",
            padding: "8px 16px",
            fontWeight: 500,
            border: "1px solid #e3f2fd",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            "&:hover": {
              backgroundColor: "#e3f2fd",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            },
            transition: "all 0.3s ease",
          }}
        >
          View Interviewer Guidelines
        </Button>
        <DialogContent sx={{ padding: "16px 24px" }}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, isValid, dirty }) => (
              <Form>
                <Typography
                  variant="h6"
                  sx={{
                    mt: 2,
                    mb: 2,
                    color: "#1e293b",
                    fontWeight: 600,
                    fontSize: "16px",
                    borderBottom: "2px solid #ede9fe",
                    paddingBottom: "8px",
                  }}
                >
                  Ratings (1-5 stars)
                </Typography>

                <RatingField
                  name="commitment_to_studies_rating"
                  label="Commitment to Studies"
                  icon={<MdSchool />}
                />

                <RatingField
                  name="leadership_potential_rating"
                  label="Leadership Potential"
                  icon={<FaUsers />}
                />

                <RatingField
                  name="community_involvement_rating"
                  label="Community Involvement"
                  icon={<FaHandshake />}
                />

                <RatingField
                  name="initiative_and_creativity_rating"
                  label="Initiative and Creativity"
                  icon={<FaLightbulb />}
                />

                <RatingField
                  name="alignment_with_scholarship_objectives_rating"
                  label="Alignment with Scholarship Objectives"
                  icon={<MdAssignment />}
                />

                <RatingField
                  name="vision_for_future_contribution_to_society_rating"
                  label="Vision for Future Contribution to Society"
                  icon={<FaUserGraduate />}
                />

                <Typography
                  variant="h6"
                  sx={{
                    mt: 3,
                    mb: 2,
                    color: "#1e293b",
                    fontWeight: 600,
                    fontSize: "16px",
                    borderBottom: "2px solid #ede9fe",
                    paddingBottom: "8px",
                  }}
                >
                  Comments
                </Typography>

                <TextFieldComponent
                  name="comments_related_to_academic_performance"
                  label="Academic Performance Comments"
                  placeholder="Comments on candidate's academic background and performance..."
                  icon={<MdSchool />}
                  rows={3}
                />

                <TextFieldComponent
                  name="comments_related_to_leadership"
                  label="Leadership Comments"
                  placeholder="Comments on candidate's leadership qualities and experiences..."
                  icon={<FaUsers />}
                  rows={3}
                />

                <TextFieldComponent
                  name="comments_related_to_values"
                  label="Values Comments"
                  placeholder="Comments on candidate's ethical values and principles..."
                  icon={<FaHandshake />}
                  rows={3}
                />

                <TextFieldComponent
                  name="final_remarks"
                  label="Final Remarks"
                  placeholder="Overall assessment and final remarks about the candidate..."
                  icon={<FaComments />}
                  rows={3}
                />

                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  spacing={2}
                  sx={{ mt: 2 }}
                >
                  <Btn
                    buttonColor={
                      theme.palette.colors["black"]["600"] as ColorsCodeType
                    }
                    textColor={
                      theme.palette.colors["white"]["400"] as ColorsCodeType
                    }
                    onClick={onClose}
                  >
                    Cancel
                  </Btn>

                  <Btn
                    type="submit"
                    disabled={
                      isSubmitting ||
                      !isValid ||
                      !dirty ||
                      submitStatus === "loading"
                    }
                  >
                    {submitStatus === "loading"
                      ? "Submitting..."
                      : "Submit Feedback"}
                  </Btn>
                </Stack>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* Rating Scale Definitions Popover */}
      <Popover
        open={openRatingInfo}
        anchorEl={anchorEl}
        onClose={handleCloseRatingInfo}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: 320,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            borderRadius: "10px",
            mt: 1,
          },
        }}
      >
        {activeRatingField && (
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1, color: "#1e293b" }}>
              Rating Scale Definitions
            </Typography>
            {ratingScaleDefinitions[
              activeRatingField as keyof typeof ratingScaleDefinitions
            ]?.map((item) => (
              <Box key={item.value} sx={{ mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: "#fff",
                      backgroundColor:
                        item.value === 1
                          ? "#ef4444"
                          : item.value === 2
                          ? "#f97316"
                          : item.value === 3
                          ? "#eab308"
                          : item.value === 4
                          ? "#22c55e"
                          : "#6366f1",
                      borderRadius: "50%",
                      mr: 1,
                    }}
                  >
                    {item.value}
                  </Box>
                  <Typography
                    sx={{ fontWeight: 600, fontSize: "14px", color: "#334155" }}
                  >
                    {item.label}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "12px", color: "#64748b", ml: 4 }}>
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Popover>


      <InterviewGuidelinesModal
        open={guidelinesOpen}
        onClose={() => setGuidelinesOpen(false)}
      />
    </>
  );
}
